import {Box3, Group, Mesh, Object3D, Object3DEventMap, Vector3} from 'three';
import {
    computeWorldBoundsExcludingAnimatedDoorParts,
    getBoxDistance,
    getDirection,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {DEFAULT_SNAPPING_DISTANCE} from 'components/customer/RoomPlanner/helpers/snapping/constants';
import {
    getRoomInteriorBounds,
    isFootprintInsideRoom,
} from 'components/customer/RoomPlanner/helpers/wallProximity';
import {SnapStrategy} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {toCandidate} from 'components/customer/RoomPlanner/helpers/snapping/strategies/candidate';

/**
 * Placeholder (appliance GLB) snapping.
 *
 * Everything placeholder-specific about snapping lives here so the cabinet
 * snapping arms (`getSnappingData`, `getFacingSnap`, `getLShapePanelSnap`,
 * ...) can be refactored without touching placeholder behaviour, and vice
 * versa. The snap router asks `isPlaceholderSnapPair` and, when true,
 * delegates to `getPlaceholderSnap`.
 *
 * Why placeholders need their own arm: their GLB pivot is centred, so the
 * corner-origin math in `getSnappingData` lands them overlapping the target
 * and the post-snap overlap check rejects the result. Instead they snap by
 * world AABB faces (mesh-only bounds, so overlay children such as
 * measurement arrows don't change the face gap), with facing and
 * side-only rules aligned to base-cabinet snapping. This applies both when
 * the placeholder is the item being dragged and when it is the target.
 */

/** Same face-gap magnet as cabinet-to-cabinet (DEFAULT_SNAPPING_DISTANCE). */
export const PlaceholderSnappingDistance = DEFAULT_SNAPPING_DISTANCE;

/** A face pair is only offered when the item is already on that side. */
const FACE_SIDE_TOLERANCE = 1;

type Facing = ReturnType<typeof getDirection>;

export type PlaceholderSnapAxes = {
    /** Snap on world ±X faces (left/right of the target). */
    x: boolean;
    /** Snap on world ±Z faces (front/back of the target). */
    z: boolean;
};

interface PlaceholderSnapCandidate {
    dist: number;
    x: number;
    z: number;
}

export const isPlaceholderProduct = (group: Object3D): boolean =>
    Boolean(group.userData?.isPlaceholder);

/**
 * True when either side of a snap pair is a placeholder, which routes the
 * pair through `getPlaceholderSnap` instead of the cabinet arms.
 * @param {Object3D} cabinetGroup Item being dragged
 * @param {Object3D} otherProduct Candidate snap target
 * @return {boolean} Whether to use placeholder snapping for this pair
 */
export const isPlaceholderSnapPair = (
    cabinetGroup: Object3D,
    otherProduct: Object3D
): boolean =>
    isPlaceholderProduct(cabinetGroup) || isPlaceholderProduct(otherProduct);

/**
 * Yaw that holds an item's facing. Placeholders store it on `rotation.y`;
 * cabinets use `rotation.z`. Cabinet arms that may still see a placeholder
 * (e.g. the return-cabinet facing path) call this instead of reading
 * `rotation.z` directly.
 * @param {Object3D} group Cabinet or placeholder
 * @return {number} Facing rotation in radians
 */
export const getFacingRotation = (group: Object3D): number =>
    isPlaceholderProduct(group) ? group.rotation.y : group.rotation.z;

/**
 * Write a placeholder's facing yaw on `rotation.y`. Cabinets keep using
 * `rotation.z` at the call site.
 * @param {Object3D} group Placeholder
 * @param {number} radians Facing rotation in radians
 * @return {void}
 */
export const setPlaceholderFacingRotation = (
    group: Object3D,
    radians: number
): void => {
    group.rotation.y = radians;
};

/**
 * Facing direction of a cabinet or placeholder, see `getFacingRotation`.
 * @param {Object3D} group Cabinet or placeholder
 * @return {Facing} 'normal' | 'right' | 'back' | 'left'
 */
export const getFacingDirection = (group: Object3D): Facing =>
    getDirection(getFacingRotation(group));

/**
 * Facing rules for a placeholder pair, matching base-cabinet snapping:
 * the two items must face the same way or be back-to-back, and contact is
 * side-only (never the front/back of the target). Facing normal/back → sides
 * are world ±X; facing left/right → sides are world ±Z.
 * @param {Object3D} cabinetGroup Item being dragged
 * @param {Object3D} otherProduct Candidate snap target
 * @return {PlaceholderSnapAxes | null} Allowed axes, or null when the pair
 * must not snap at all
 */
export const getPlaceholderSnapAxes = (
    cabinetGroup: Object3D,
    otherProduct: Object3D
): PlaceholderSnapAxes | null => {
    const selectedDir = getFacingDirection(cabinetGroup);
    const otherDir = getFacingDirection(otherProduct);

    const isSameFacing = selectedDir === otherDir;
    const isBackToBack =
        (selectedDir === 'back' && otherDir === 'normal') ||
        (selectedDir === 'normal' && otherDir === 'back') ||
        (selectedDir === 'left' && otherDir === 'right') ||
        (selectedDir === 'right' && otherDir === 'left');

    if (!isSameFacing && !isBackToBack) {
        return null;
    }

    const sideSnapOnX = selectedDir === 'normal' || selectedDir === 'back';

    return {x: sideSnapOnX, z: !sideSnapOnX};
};

/**
 * Mesh-only world AABB used for placeholder snapping on both sides of the
 * pair, so overlay children don't move the faces being matched.
 * @param {Group} group Cabinet or placeholder
 * @return {Box3} World-space bounds
 */
const getPlaceholderSnapBounds = (group: Group<Object3DEventMap>): Box3 =>
    computeWorldBoundsExcludingAnimatedDoorParts(group);

/**
 * AABB face-to-face snap for a pair involving a placeholder.
 *
 * Offers a face pair only when the dragged item is already on that side of
 * the target (within `FACE_SIDE_TOLERANCE`) and the resulting pose fits
 * between the walls, so a target flush in a corner cannot offer its wall
 * face. Along the contact axis the item is made flush; along the other axis
 * it is aligned to whichever of the target's min/max faces is nearer.
 *
 * @param {Group} cabinetGroup Item being dragged (cabinet or placeholder)
 * @param {Group} otherProduct Snap target (cabinet or placeholder)
 * @param {Vector3} targetPos Requested drag position
 * @param {Vector3} currentPos Current world position of `cabinetGroup`
 * @param {Object3D | null} wallGroup Room walls for the fits-in-room check;
 * falls back to the parent of `userData.currentWall`
 * @return {Vector3 | null} Snapped position, or null when no face is in range
 */
export const getPlaceholderSnap = (
    cabinetGroup: Group<Object3DEventMap>,
    otherProduct: Group<Object3DEventMap>,
    targetPos: Vector3,
    currentPos: Vector3,
    wallGroup?: Object3D | null
): Vector3 | null => {
    const axes = getPlaceholderSnapAxes(cabinetGroup, otherProduct);
    if (!axes) return null;

    const snapDistance = PlaceholderSnappingDistance;

    const selectedBox = getPlaceholderSnapBounds(cabinetGroup);
    const otherBox = getPlaceholderSnapBounds(otherProduct);
    const delta = targetPos.clone().sub(currentPos);
    const shiftedBox = selectedBox.clone().translate(delta);

    const boxDist = getBoxDistance(shiftedBox, otherBox);

    // Boxes must be close on at least one allowed axis to consider snapping.
    if (
        (axes.x ? boxDist.z >= snapDistance : true) &&
        (axes.z ? boxDist.x >= snapDistance : true)
    ) {
        return null;
    }

    const alignedZ = (): number => {
        const distMinMin = Math.abs(shiftedBox.min.z - otherBox.min.z);
        const distMaxMax = Math.abs(shiftedBox.max.z - otherBox.max.z);

        return distMinMin <= distMaxMax
            ? targetPos.z + (otherBox.min.z - shiftedBox.min.z)
            : targetPos.z + (otherBox.max.z - shiftedBox.max.z);
    };

    const alignedX = (): number => {
        const distMinMin = Math.abs(shiftedBox.min.x - otherBox.min.x);
        const distMaxMax = Math.abs(shiftedBox.max.x - otherBox.max.x);

        return distMinMin <= distMaxMax
            ? targetPos.x + (otherBox.min.x - shiftedBox.min.x)
            : targetPos.x + (otherBox.max.x - shiftedBox.max.x);
    };

    const candidates: PlaceholderSnapCandidate[] = [];

    if (axes.x && boxDist.z < snapDistance) {
        if (shiftedBox.max.x <= otherBox.min.x + FACE_SIDE_TOLERANCE) {
            candidates.push({
                dist: Math.abs(shiftedBox.max.x - otherBox.min.x),
                x: targetPos.x + (otherBox.min.x - shiftedBox.max.x),
                z: alignedZ(),
            });
        }
        if (shiftedBox.min.x >= otherBox.max.x - FACE_SIDE_TOLERANCE) {
            candidates.push({
                dist: Math.abs(shiftedBox.min.x - otherBox.max.x),
                x: targetPos.x + (otherBox.max.x - shiftedBox.min.x),
                z: alignedZ(),
            });
        }
    }

    if (axes.z && boxDist.x < snapDistance) {
        if (shiftedBox.max.z <= otherBox.min.z + FACE_SIDE_TOLERANCE) {
            candidates.push({
                dist: Math.abs(shiftedBox.max.z - otherBox.min.z),
                x: alignedX(),
                z: targetPos.z + (otherBox.min.z - shiftedBox.max.z),
            });
        }
        if (shiftedBox.min.z >= otherBox.max.z - FACE_SIDE_TOLERANCE) {
            candidates.push({
                dist: Math.abs(shiftedBox.min.z - otherBox.max.z),
                x: alignedX(),
                z: targetPos.z + (otherBox.max.z - shiftedBox.min.z),
            });
        }
    }

    const interior = getRoomInteriorBounds(
        wallGroup ?? (cabinetGroup.userData.currentWall as Mesh | null)?.parent
    );

    const footprint = new Box3();
    const move = new Vector3();
    let best: PlaceholderSnapCandidate | null = null;

    for (const candidate of candidates) {
        if (candidate.dist >= snapDistance) continue;
        if (best && candidate.dist >= best.dist) continue;

        if (interior) {
            move.set(candidate.x - targetPos.x, 0, candidate.z - targetPos.z);
            const fits = isFootprintInsideRoom(
                footprint.copy(shiftedBox).translate(move),
                interior
            );
            if (!fits) continue;
        }

        best = candidate;
    }

    if (!best) return null;

    return new Vector3(best.x, targetPos.y, best.z);
};

/**
 * Router entry for a placeholder pair. The cabinet strategies stay untouched;
 * this only adapts `getPlaceholderSnap` to the strategy result shape.
 * @param {THREE.Group} cabinetGroup Item being dragged
 * @param {THREE.Group} otherProduct Snap target
 * @param {SnapContext} ctx Drag context from the router
 * @return {SnapCandidate|null} The snapped candidate, or null when out of range
 */
export const getPlaceholderSnapStrategy: SnapStrategy = (
    cabinetGroup,
    otherProduct,
    ctx
) =>
    toCandidate(
        getPlaceholderSnap(
            cabinetGroup,
            otherProduct,
            ctx.targetPos,
            ctx.currentPos,
            ctx.wallGroup
        ),
        ctx.targetPos
    );
